import React from "react";
import {
  Box,
  Button,
  Checkbox,
  HStack,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { useFieldArray, useWatch } from "react-hook-form";
import { BsArrowDown, BsArrowUp, BsFiles, BsPlus, BsTrash } from "react-icons/bs";
import { cloneTemplateItem, createTemplateItem } from "./templateSectionUtils";

const RatingCriteriaItemsTable = ({ control, sectionIndex, register, sectionTitle, setValue }) => {
  const columnHeaderBg = useColorModeValue("blue.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mobileRowBg = useColorModeValue("white", "gray.800");
  const labelColor = useColorModeValue("gray.600", "gray.400");
  const { fields, append, remove, move, insert } = useFieldArray({
    control,
    name: `sections.${sectionIndex}.items`,
    keyName: "fieldKey",
  });
  const items = useWatch({ control, name: `sections.${sectionIndex}.items` }) || [];

  const addRow = () => append(createTemplateItem({ sequence: fields.length + 1 }));
  const duplicateRow = (index) =>
    insert(index + 1, cloneTemplateItem(items[index], { sequence: index + 2 }));

  React.useEffect(() => {
    fields.forEach((_, index) => {
      if (items[index]?.sequence !== index + 1) {
        setValue(`sections.${sectionIndex}.items.${index}.sequence`, index + 1, { shouldDirty: true });
      }
    });
  }, [fields, items, sectionIndex, setValue]);

  const addLabel = sectionTitle?.toLowerCase().includes("behaviour")
    ? "Add behaviour parameter"
    : sectionTitle?.toLowerCase().includes("discipline")
      ? "Add discipline parameter"
      : "Add growth parameter";

  return (
    <>
      <VStack display={{ base: "flex", md: "none" }} align="stretch" spacing={3}>
        {fields.map((field, index) => (
          <Box
            key={field.fieldKey}
            bg={mobileRowBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="md"
            p={3}
          >
            <HStack justify="space-between" align="center" mb={3}>
              <Text fontSize="sm" fontWeight="semibold">Parameter {index + 1}</Text>
              <HStack spacing={1}>
                <IconButton size="xs" variant="ghost" icon={<BsArrowUp />} aria-label="Move row up" onClick={() => index > 0 && move(index, index - 1)} />
                <IconButton size="xs" variant="ghost" icon={<BsArrowDown />} aria-label="Move row down" onClick={() => index < fields.length - 1 && move(index, index + 1)} />
                <IconButton size="xs" variant="ghost" icon={<BsFiles />} aria-label="Duplicate row" onClick={() => duplicateRow(index)} />
                <IconButton size="xs" variant="ghost" colorScheme="red" icon={<BsTrash />} aria-label="Delete row" onClick={() => remove(index)} />
              </HStack>
            </HStack>
            <VStack align="stretch" spacing={3}>
              <Box>
                <Text mb={1} fontSize="xs" color={labelColor} fontWeight="medium">Parameter</Text>
                <Input size="sm" placeholder="Parameter" {...register(`sections.${sectionIndex}.items.${index}.title`)} />
              </Box>
              <Box>
                <Text mb={1} fontSize="xs" color={labelColor} fontWeight="medium">Behaviour Indicator</Text>
                <Textarea size="sm" minH="84px" placeholder="Behaviour indicator" {...register(`sections.${sectionIndex}.items.${index}.description`)} />
              </Box>
              <Checkbox size="sm" {...register(`sections.${sectionIndex}.items.${index}.ratingRequired`)}>Rating required</Checkbox>
              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                <Box>
                  <Text mb={1} fontSize="xs" color={labelColor} fontWeight="medium">Comments</Text>
                  <Select size="sm" {...register(`sections.${sectionIndex}.items.${index}.commentRule`)}>
                    <option value="NONE">None</option>
                    <option value="OPTIONAL">Optional</option>
                    <option value="REQUIRED">Required</option>
                  </Select>
                </Box>
                <Box>
                  <Text mb={1} fontSize="xs" color={labelColor} fontWeight="medium">Evidence</Text>
                  <Select size="sm" {...register(`sections.${sectionIndex}.items.${index}.evidenceRule`)}>
                    <option value="NONE">None</option>
                    <option value="OPTIONAL">Optional</option>
                    <option value="REQUIRED">Required</option>
                  </Select>
                </Box>
              </SimpleGrid>
            </VStack>
          </Box>
        ))}
      </VStack>

      <TableContainer display={{ base: "none", md: "block" }} overflowX="auto">
        <Table size="sm">
          <Thead bg={columnHeaderBg}>
            <Tr>
              <Th width="52px">#</Th>
              <Th minW="220px">Parameter</Th>
              <Th minW="360px">Behaviour Indicator</Th>
              <Th width="130px">Rating Required</Th>
              <Th width="130px">Comments</Th>
              <Th width="130px">Evidence</Th>
              <Th width="140px">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {fields.map((field, index) => (
              <Tr key={field.fieldKey}>
                <Td>{index + 1}</Td>
                <Td><Input size="sm" placeholder="Parameter" {...register(`sections.${sectionIndex}.items.${index}.title`)} /></Td>
                <Td><Textarea size="sm" minH="72px" placeholder="Behaviour indicator" {...register(`sections.${sectionIndex}.items.${index}.description`)} /></Td>
                <Td><Checkbox {...register(`sections.${sectionIndex}.items.${index}.ratingRequired`)} /></Td>
                <Td>
                  <Select size="sm" {...register(`sections.${sectionIndex}.items.${index}.commentRule`)}>
                    <option value="NONE">None</option>
                    <option value="OPTIONAL">Optional</option>
                    <option value="REQUIRED">Required</option>
                  </Select>
                </Td>
                <Td>
                  <Select size="sm" {...register(`sections.${sectionIndex}.items.${index}.evidenceRule`)}>
                    <option value="NONE">None</option>
                    <option value="OPTIONAL">Optional</option>
                    <option value="REQUIRED">Required</option>
                  </Select>
                </Td>
                <Td>
                  <HStack spacing={1}>
                    <IconButton size="xs" variant="ghost" icon={<BsArrowUp />} aria-label="Move row up" onClick={() => index > 0 && move(index, index - 1)} />
                    <IconButton size="xs" variant="ghost" icon={<BsArrowDown />} aria-label="Move row down" onClick={() => index < fields.length - 1 && move(index, index + 1)} />
                    <IconButton size="xs" variant="ghost" icon={<BsFiles />} aria-label="Duplicate row" onClick={() => duplicateRow(index)} />
                    <IconButton size="xs" variant="ghost" colorScheme="red" icon={<BsTrash />} aria-label="Delete row" onClick={() => remove(index)} />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      <Button mt={3} size="sm" variant="outline" leftIcon={<BsPlus />} onClick={addRow} width={{ base: "100%", sm: "auto" }}>{addLabel}</Button>
    </>
  );
};

export default RatingCriteriaItemsTable;
